import BlockSharpIcon from "@mui/icons-material/BlockSharp";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import * as React from "react";
import { api, state, ui } from "../../../../../interfaces/interfaces";
import "./index.css";
import { authService } from "@src/services/authorizationService";
import { useAppSelector } from "@store/hooks";

const loadCallMeBack = async (leadId: number) => {
	const lock = await api.fetchGetCallMeBackLock({
		id: leadId,
		objectTypeId: 3,
	});
	const currentUser = authService._user;
	const currentUserName = `${currentUser?.profile?.first_name} ${currentUser?.profile?.last_name}`;
	if (lock && lock.length > 0 && lock[0].name !== currentUserName) {
		// display ui message warning about lock
		ui.ProblemMsg = `This call me back is currently locked by another user (${lock[0].name}). Please try again later.`;
		ui.ProblemMsgVariant = "warning";
		ui.ProblemShowMsg = true;
		return;
	}
	await api.fetchSetCallMeBackLock({
		id: leadId,
		objectTypeId: 3,
		userId: state.UserId,
	});
	const data = await api.fetchGetCallMeBack(leadId);
	const dataNotes = await api.fetchGetCallMeBackNotes(leadId);
	// merge objects
	const mergedData = { ...data?.[0], notes: dataNotes };
	if (mergedData) {
		generateFormData(mergedData);
	}
};

const generateFormData = (data: any) => {
	// if data is array, then get first element
	data = data instanceof Array ? data[0] : data;

	const {
		title,
		leadId,
		forename,
		surname,
		contactNumber,
		email,
		accountCode,
		sortDate,
		description,
		followUpNotes,
		sentSMS,
		sentEmail,
		callAttempts,
		dateTimeLastCalled,
		dateTimeSMSSent,
		dateTimeEmailSent,
		removed,
		isLocked,
		userId,
		username,
		dateTimeEnquiry,
		dateTimeFollowUp,
		dateTimeUpdated,
		notes,
	} = data;

	const formData = {
		title,
		leadId,
		forename,
		sortDate,
		surname,
		contactNumber,
		email,
		accountCode,
		description,
		followUpNotes,
		sentSMS,
		sentEmail,
		callAttempts,
		dateTimeLastCalled,
		dateTimeSMSSent,
		dateTimeEmailSent,
		removed,
		isLocked,
		userId,
		username,
		dateTimeEnquiry,
		dateTimeFollowUp,
		dateTimeUpdated,
		notes,
	};

	ui.CallMeBacksModalData = formData;
	ui.CallMeBacksModal = true;
};

type BlinksClassNames =
	| "blink-text-red"
	| "blink-text-orange"
	| "blink-text-green"
	| "no-blink-white";

const getBlinkClass = (date: string) => {
	let className: BlinksClassNames = "no-blink-white";
	const now = new Date();
	const diff = now.getTime() - new Date(date).getTime();
	const minutes = Math.floor(diff / (1000 * 60)); // minutes
	if (minutes > 30) {
		className = "blink-text-red";
		return className;
	}
	if (minutes > 15) {
		className = "blink-text-orange";
		return className;
	}
	if (minutes > 0) {
		className = "blink-text-green";
		return className;
	}
	return className;
};

const DenseTable: React.FC = () => {
	// state for queue
	const queue = useAppSelector(({ ui }) => ui.CallMeBacksQueue);

	return (
		<TableContainer
			component={Paper}
			sx={{
				maxHeight: "15rem",
				ml: "-16px !important",
				mt: "0px !important",
				width: "calc(100% + 16px)",
			}}
		>
			<Table size="small" aria-label="a dense table" stickyHeader>
				<TableHead>
					<TableRow>
						<TableCell
							sx={{
								backgroundColor: "#2e3add",
								border: "1px solid #ffffff82",
								color: "#fff",
								whiteSpace: "nowrap",
							}}
						>
							Total: {queue?.length}
						</TableCell>
						<TableCell
							sx={{
								backgroundColor: "#2e3add",
								border: "1px solid #ffffff82",
								color: "#fff",
							}}
							align="left"
						>
							Received
						</TableCell>
						<TableCell
							sx={{
								backgroundColor: "#2e3add",
								border: "1px solid #ffffff82",
								color: "#fff",
							}}
							align="left"
						>
							Status
						</TableCell>
						<TableCell
							sx={{
								backgroundColor: "#2e3add",
								border: "1px solid #ffffff82",
								color: "#fff",
							}}
							align="left"
						>
							Called
						</TableCell>
						<TableCell
							sx={{
								backgroundColor: "#2e3add",
								border: "1px solid #ffffff82",
								color: "#fff",
							}}
							align="left"
						>
							Next Action
						</TableCell>
						<TableCell
							sx={{
								backgroundColor: "#2e3add",
								border: "1px solid #ffffff82",
								color: "#fff",
							}}
							align="left"
						>
							Summary
						</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{queue &&
						queue.length > 0 &&
						queue.map((row: any, idx: any) => (
							<TableRow
								key={row.id}
								className={getBlinkClass(row.sortDate)}
								sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
								onClick={() => {
									loadCallMeBack(Number(row.id));
								}}
							>
								<TableCell
									component="th"
									scope="row"
									sx={{ whiteSpace: "nowrap" }}
								>
									{row.isLocked ? <BlockSharpIcon /> : idx + 1}
								</TableCell>
								<TableCell align="left" sx={{ whiteSpace: "nowrap" }}>
									{new Date(row.dateTimeEnquiry).toLocaleDateString("en-GB") +
										" " +
										new Date(row.dateTimeEnquiry)
											.toLocaleTimeString("en-GB")
											.substring(0, 5)}
								</TableCell>
								<TableCell sx={{ whiteSpace: "nowrap" }} align="left">
									{row.statusDescription}
								</TableCell>
								<TableCell sx={{ whiteSpace: "nowrap" }} align="left">
									{row.callAttempts ?? 0}
								</TableCell>
								<TableCell sx={{ whiteSpace: "nowrap" }} align="left">
									{row.dateTimeFollowUp
										? new Date(row.dateTimeFollowUp).toLocaleDateString(
												"en-GB",
											) +
											" " +
											new Date(row.dateTimeFollowUp)
												.toLocaleTimeString("en-GB")
												.substring(0, 5)
										: ""}
								</TableCell>
								<TableCell align="left" sx={{ whiteSpace: "nowrap" }}>
									{row.description.substring(0, 100) + "..."}
								</TableCell>
							</TableRow>
						))}
				</TableBody>
			</Table>
		</TableContainer>
	);
};

export default React.memo(DenseTable);
